import type { FeedSource, RoutingRule } from './api';

/**
 * Frontend-only form helpers for the admin SPA.
 * Mirrors the admin Lambda's closed sets and validation invariants without
 * importing backend modules into the Vite bundle.
 */

export const CATEGORIES = ['person', 'publication', 'channel', 'feed'] as const;
export const PRIORITIES = ['critical', 'high', 'normal', 'low'] as const;
export const SOURCE_TYPES = [
  'rss',
  'youtube_rss',
  'youtube_api',
  'twitter',
  'bluesky',
  'mastodon',
  'blog',
  'newsletter',
  'podcast',
  'sitemap',
] as const;
export const LICENCE_CLASSES = [
  'public_rss',
  'paywalled_rss',
  'personal_only',
  'do_not_quote',
] as const;
export const ROUTING_DESTINATIONS = [
  'digest',
  'releases',
  'world',
  'archive',
  'telegram',
  'discord',
  'blog_drafts',
] as const;

export type FeedSourceType = (typeof SOURCE_TYPES)[number];
export type LicenceClass = (typeof LICENCE_CLASSES)[number];
export type RoutingDestination = (typeof ROUTING_DESTINATIONS)[number];

type SourceCategory = (typeof CATEGORIES)[number];
type SourcePriority = (typeof PRIORITIES)[number];
type MutableFeedSourcePatch = { -readonly [K in keyof FeedSource]?: FeedSource[K] };
type MutableRulePatch = { -readonly [K in keyof RoutingRule]?: RoutingRule[K] };

export type SourceFormState = {
  name: string;
  category: SourceCategory | '';
  priority: SourcePriority;
  source_type: FeedSourceType | '';
  licence_class: LicenceClass;
  poll_interval_minutes: number;
  active: boolean;
  tags: readonly string[];
  expertise: string | null;
  rss_url: string | null;
  youtube_channel_id: string | null;
  youtube_handle: string | null;
  twitter_handle: string | null;
  bluesky_handle: string | null;
  mastodon_url: string | null;
  blog_url: string | null;
  newsletter_url: string | null;
  website_url: string | null;
  sitemap_url_filter: string | null;
};

export type RuleFormState = {
  name: string;
  match_expression_raw: string;
  match_expression_value: Record<string, unknown> | null;
  destinations: readonly RoutingDestination[];
  licence_class_override: LicenceClass | null;
  active: boolean;
  priority: number | null;
};

export const SOURCE_TYPE_URL_FIELD: Record<FeedSourceType, keyof FeedSource | null> = {
  rss: 'rss_url',
  youtube_rss: 'rss_url',
  youtube_api: null,
  twitter: 'twitter_handle',
  bluesky: 'bluesky_handle',
  mastodon: 'mastodon_url',
  blog: 'blog_url',
  newsletter: 'newsletter_url',
  podcast: 'rss_url',
  // sitemap reuses website_url to hold the sitemap URL (no new column needed).
  sitemap: 'website_url',
};

const SOURCE_OPTIONAL_TEXT_FIELDS = [
  'expertise',
  'rss_url',
  'youtube_channel_id',
  'youtube_handle',
  'twitter_handle',
  'bluesky_handle',
  'mastodon_url',
  'blog_url',
  'newsletter_url',
  'website_url',
  'sitemap_url_filter',
] as const;

const SOURCE_EDITABLE_FIELDS = [
  'name',
  'category',
  'priority',
  'source_type',
  'licence_class',
  'poll_interval_minutes',
  'active',
  'tags',
  'expertise',
  'rss_url',
  'youtube_channel_id',
  'youtube_handle',
  'twitter_handle',
  'bluesky_handle',
  'mastodon_url',
  'blog_url',
  'newsletter_url',
  'website_url',
  'sitemap_url_filter',
] as const;

const RULE_DESTINATION_SET = new Set<string>(ROUTING_DESTINATIONS);
type SourceEditableField = (typeof SOURCE_EDITABLE_FIELDS)[number];

export function requiredUrlFieldFor(sourceType: FeedSourceType): keyof FeedSource | null {
  return SOURCE_TYPE_URL_FIELD[sourceType];
}

export function parseTagsInput(raw: string): readonly string[] {
  if (raw.trim().length === 0) {
    return [];
  }
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

export function formatTagsInput(tags: readonly string[]): string {
  return tags.join(', ');
}

export function parseMatchExpression(
  raw: string,
): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
  if (raw.trim().length === 0) {
    return { ok: true, value: {} };
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isPlainRecord(parsed)) {
      return {
        ok: false,
        error: 'match_expression must be a JSON object, not an array, string, number, or null',
      };
    }
    return { ok: true, value: parsed };
  } catch (error) {
    return {
      ok: false,
      error: `invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

export function diffSourceUpdate(original: FeedSource, draft: SourceFormState): Partial<FeedSource> {
  const next = sourceFormStateToPayload(draft);
  const diff: MutableFeedSourcePatch = {};

  for (const field of SOURCE_EDITABLE_FIELDS) {
    if (field === 'tags') {
      if (!sameStringSet(original.tags, next.tags ?? [])) {
        diff.tags = next.tags;
      }
      continue;
    }

    setSourceFieldIfChanged(diff, field, original, next);
  }

  return diff;
}

export function diffRuleUpdate(original: RoutingRule, draft: RuleFormState): Partial<RoutingRule> {
  const next = ruleFormStateToPayload(draft);
  const diff: MutableRulePatch = {};

  if (next.name !== original.name.trim()) {
    diff.name = next.name;
  }
  if (!sameJsonValue(original.match_expression, next.match_expression ?? {})) {
    diff.match_expression = next.match_expression ?? {};
  }
  if (!sameStringSet(original.destinations, next.destinations ?? [])) {
    diff.destinations = next.destinations;
  }
  if ((next.licence_class_override ?? null) !== original.licence_class_override) {
    diff.licence_class_override = next.licence_class_override;
  }
  if ((next.priority ?? null) !== original.priority) {
    diff.priority = next.priority;
  }
  if ((next.active ?? original.active) !== original.active) {
    diff.active = next.active;
  }

  return diff;
}

export function sourceFormStateToPayload(draft: SourceFormState): Partial<FeedSource> {
  const payload: MutableFeedSourcePatch = {
    name: draft.name.trim(),
    priority: draft.priority,
    licence_class: draft.licence_class,
    poll_interval_minutes: draft.poll_interval_minutes,
    active: draft.active,
    tags: normaliseTagsArray(draft.tags),
  };

  if (draft.category !== '') {
    payload.category = draft.category;
  }
  if (draft.source_type !== '') {
    payload.source_type = draft.source_type;
  }

  for (const field of SOURCE_OPTIONAL_TEXT_FIELDS) {
    payload[field] = normaliseOptionalText(draft[field]);
  }

  return payload;
}

export function ruleFormStateToPayload(draft: RuleFormState): Partial<RoutingRule> {
  const destinations = draft.destinations.filter((destination) => RULE_DESTINATION_SET.has(destination));
  return {
    name: draft.name.trim(),
    match_expression: draft.match_expression_value ?? {},
    destinations,
    licence_class_override: draft.licence_class_override,
    active: draft.active,
    priority: normalisePriority(draft.priority),
  };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normaliseOptionalText(value: string | null): string | null {
  if (value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function normaliseTagsArray(tags: readonly string[]): readonly string[] {
  return tags
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

function normalisePriority(priority: number | null): number | null {
  if (priority === null || !Number.isFinite(priority)) {
    return null;
  }
  return priority;
}

function sameStringSet(left: readonly string[], right: readonly string[]): boolean {
  const leftSorted = [...new Set(left.map((item) => item.trim()).filter((item) => item.length > 0))].sort();
  const rightSorted = [...new Set(right.map((item) => item.trim()).filter((item) => item.length > 0))].sort();
  return JSON.stringify(leftSorted) === JSON.stringify(rightSorted);
}

function sameJsonValue(left: Record<string, unknown>, right: Record<string, unknown>): boolean {
  return stableJson(left) === stableJson(right);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(',')}]`;
  }
  if (isPlainRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
}

function setSourceFieldIfChanged<K extends Exclude<SourceEditableField, 'tags'>>(
  diff: MutableFeedSourcePatch,
  field: K,
  original: FeedSource,
  next: Partial<FeedSource>,
): void {
  const current = original[field];
  const updated = next[field];
  if (updated !== current) {
    diff[field] = updated;
  }
}

// ---------------------------------------------------------------------------
// FeedSource → SourceFormState — used when opening the Edit drawer to seed the
// form with the row's current values. RoutingRule → RuleFormState mirrors.
// ---------------------------------------------------------------------------

export function feedSourceToFormState(s: FeedSource): SourceFormState {
  return {
    name: s.name,
    category: s.category,
    priority: s.priority,
    source_type: s.source_type as FeedSourceType,
    licence_class: s.licence_class,
    poll_interval_minutes: s.poll_interval_minutes,
    active: s.active,
    tags: s.tags,
    expertise: s.expertise,
    rss_url: s.rss_url,
    youtube_channel_id: s.youtube_channel_id,
    youtube_handle: s.youtube_handle,
    twitter_handle: s.twitter_handle,
    bluesky_handle: s.bluesky_handle,
    mastodon_url: s.mastodon_url,
    blog_url: s.blog_url,
    newsletter_url: s.newsletter_url,
    website_url: s.website_url,
    sitemap_url_filter: s.sitemap_url_filter,
  };
}

export function routingRuleToFormState(r: RoutingRule): RuleFormState {
  return {
    name: r.name,
    match_expression_raw: JSON.stringify(r.match_expression, null, 2),
    match_expression_value: r.match_expression,
    destinations: r.destinations.filter((d): d is RoutingDestination =>
      ROUTING_DESTINATIONS.includes(d as RoutingDestination),
    ),
    licence_class_override: (r.licence_class_override ?? null) as LicenceClass | null,
    active: r.active,
    priority: r.priority,
  };
}

export function emptyRuleFormState(): RuleFormState {
  return {
    name: '',
    match_expression_raw: '{}',
    match_expression_value: {},
    destinations: ['archive'],
    licence_class_override: null,
    active: true,
    priority: null,
  };
}
